import React, { useState } from 'react';
import { Flex, Div, Gutter, Title, BodyText, GridBag } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { removeEntityByIndex, updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

interface KeySellingPointsProps {
  title: string;
  keySellingPoints: string[] | undefined;
  keySellingPointsTranslations: { localeId: number; translations: string[] }[] | undefined;
  locale: LocaleResponse;
  handleKeySellingPointsChange: (keySellingPoints: string[]) => void;
  handleKeySellingPointsTranslationsChange: (translations: { localeId: number; translations: string[] }[]) => void;
  errors: Errors['keySellingPoints'];
}

function KeySellingPoints(props: KeySellingPointsProps) {
  const {
    title,
    keySellingPoints,
    keySellingPointsTranslations,
    locale,
    handleKeySellingPointsChange,
    handleKeySellingPointsTranslationsChange,
    errors,
  } = props;
  const { t } = useTranslation('productscreen');

  const [referenceInputsOpen, setReferenceInputsOpen] = useState<boolean[]>(new Array(keySellingPoints?.length).fill(false));

  const setReferenceInputOpenState = (index: number, open: boolean) => {
    const openReferenceInputsState = [...referenceInputsOpen];
    openReferenceInputsState[index] = open;
    setReferenceInputsOpen(openReferenceInputsState);
  };

  const handleKeySellingPointAdd = () => {
    const updatedKeySellingPoints = [...(keySellingPoints || []), ''];
    const updatedTranslations = keySellingPointsTranslations?.map(translation => {
      return {
        ...translation,
        translations: [...(translation.translations || []), ''],
      };
    });
    handleKeySellingPointsChange(updatedKeySellingPoints);
    if (updatedTranslations) handleKeySellingPointsTranslationsChange(updatedTranslations);
    setReferenceInputsOpen([...referenceInputsOpen, false]);
  };

  const handleKeySellingPointRemoval = (index: number) => {
    const updatedKeySellingPoints = removeEntityByIndex(keySellingPoints, index);
    const updatedTranslations = keySellingPointsTranslations?.map(translation => {
      return {
        ...translation,
        translations: removeEntityByIndex(translation.translations, index),
      };
    });
    handleKeySellingPointsChange(updatedKeySellingPoints);
    if (updatedTranslations) handleKeySellingPointsTranslationsChange(updatedTranslations);
    const openReferenceInputsState = removeEntityByIndex(referenceInputsOpen, index);
    setReferenceInputsOpen(openReferenceInputsState);
  };

  const handleKeySellingPointsUpdate = (index: number, updatedKeySellingPoint: string) => {
    const updatedKeySellingPoints = updateEntityByIndex(keySellingPoints, index, updatedKeySellingPoint);
    handleKeySellingPointsChange(updatedKeySellingPoints);
  };

  const handleTranslationKeySellingPointsChange = (index: number, updatedKeySellingPointTranslation: string) => {
    const translationIndex = (keySellingPointsTranslations || []).findIndex(translation => translation.localeId === locale.id);
    if (translationIndex !== -1 && keySellingPointsTranslations) {
      const translation = keySellingPointsTranslations[translationIndex];
      const updatedKeySellingPointTranslations = updateEntityByIndex(translation.translations, index, updatedKeySellingPointTranslation);
      const updatedTranslation = { ...translation, translations: updatedKeySellingPointTranslations };
      const updatedTranslations = updateEntityByIndex(keySellingPointsTranslations, translationIndex, updatedTranslation);
      handleKeySellingPointsTranslationsChange(updatedTranslations);
    }
  };

  const currentKeySellingPointsTranslation = keySellingPointsTranslations?.find(translation => translation.localeId === locale.id)?.translations;

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('key_sellingpoints_section.info')}</BodyText>
      <Gutter />
      {errors?.keySellingPointsGeneral && (
        <BodyText size="sm" color="red600">
          {errors.keySellingPointsGeneral}
        </BodyText>
      )}
      {(currentKeySellingPointsTranslation || []).map((keySellingPoint, index) => (
        <Div key={index}>
          <GridBag>
            <GridBag.Item md={7}>
              <EditableItem
                backgroundHoverColor="gray200"
                backgroundColor="gray100"
                divProps={{
                  padding: { size: 'sm' },
                }}
              >
                <EditableItem.Input
                  value={keySellingPoint}
                  edit={true}
                  inputProps={{
                    name: `keySellingPointTranslation-${index}`,
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleTranslationKeySellingPointsChange(index, e.target.value),
                    placeholder: t('key_sellingpoints_section.point_translation_placeholder', { language: locale.name.split(' ')[0] }),
                    label: t('key_sellingpoints_section.point_translation_label', { index: index + 1 }),
                    onKeyDown: () => undefined,
                  }}
                />
                <Gutter size="sm" />
                <EditableItem.Input
                  value={keySellingPoints?.[index]}
                  edit={referenceInputsOpen[index]}
                  isReference={true}
                  focusOnEdit={true}
                  showDelete
                  inputProps={{
                    name: `keySellingPoint-${index}`,
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      handleKeySellingPointsUpdate(index, e.target.value);
                      setReferenceInputOpenState(index, false);
                    },
                    placeholder: t('key_sellingpoints_section.point_reference_placeholder'),
                    label: t('key_sellingpoints_section.point_reference_label', { index: index + 1 }),
                    onKeyDown: () => undefined,
                    required: true,
                    errorText: errors?.keySellingPoints?.find(keySellingPointError => keySellingPointError.index === index)?.error,
                  }}
                  editIconProps={{
                    'data-testid': `keySellingPoint-${index}-edit`,
                    onClick: () => setReferenceInputOpenState(index, true),
                  }}
                  deleteIconProps={{
                    'data-testid': `keySellingPoint-${index}-delete`,
                    onClick: () => handleKeySellingPointRemoval(index),
                  }}
                />
              </EditableItem>
            </GridBag.Item>
          </GridBag>
          <Gutter size="sm" />
        </Div>
      ))}
      <Div>
        <BodyText clickableAppearance={true} color="purple500" onClick={() => handleKeySellingPointAdd()}>
          {`+ ${t('key_sellingpoints_section.add_key_sellingpoint')}`}
        </BodyText>
      </Div>
    </Flex>
  );
}

export default React.memo(KeySellingPoints);
